import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { CommunicationTemplateChannel } from '@client/components/communication-templates/CommunicationTemplateChannel';
import { CommunicationTemplateFilters } from '@client/components/communication-templates/CommunicationTemplateFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { formatCommTemplateRelatedTo } from '@client/utils/formatters/comm-template';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { useState } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/communication/comm-templates')({
    staticData: {
        metadata: {
            title: 'Communication templates',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CommunicationTemplateSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['commTemplate']['listCommTemplates'];

const columnHelper = createColumnHelper<
    RouterOutputs['commTemplate']['listCommTemplates']['rows'][number],
    QueryParams['orderBy']
>();

function CommunicationTemplateSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.commTemplate.deleteCommTemplate.useMutation();
    const navigate = useNavigate();

    const [input, setInput] = useState<QueryParams>({
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending, error } = trpc.commTemplate.listCommTemplates.useQuery(input);

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('channel', {
                id: 'channel',
                header: 'Channel',
                sortable: 'channel',
                cell: ({ row }) => <CommunicationTemplateChannel channel={row.channel} />,
            }),
            columnHelper.accessor('relatedTo', {
                id: 'relatedTo',
                header: 'Related to',
                sortable: 'relatedTo',
                cell: ({ row }) => formatCommTemplateRelatedTo(row.relatedTo),
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ row }) => (row.status ? <StatusBadge status={row.status} module="commTemplate" /> : null),
            }),
            columnHelper.accessor('version', {
                id: 'version',
                header: 'Version',
                sortable: 'version',
                cell: ({ row }) => (row.version ? <Tag>v{row.version}</Tag> : null),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.Delete
                        onConfirm={(callback) => {
                            mutate(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        void trpcUtils.commTemplate.invalidate();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    />
                ),
            }),
        ],
    });

    return (
        <TRPCErrorBoundary error={error}>
            <Outlet />

            <TableToolbar
                filters={
                    <CommunicationTemplateFilters
                        urlKey="communication-template-search-filters"
                        onChange={(where) => setInput((prev) => ({ ...prev, where, offset: 0 }))}
                    />
                }
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/org-settings/communication/comm-templates/add"
                        params={{ orgId }}
                        variant="primary"
                        label="New communication template"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/org-settings/communication/comm-templates/edit/$communicationTemplateId',
                                params: { orgId, communicationTemplateId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </TRPCErrorBoundary>
    );
}
